import React, { useState, useEffect } from "react";
import axios from "axios";

// Custom tool to reassign medical drops (shipment_type='medical', type='DROP')
// from one partner to another. Backend: /api/medical-order-mover/{partners,search,move}.

const STATUSES = [
  "", "DELIVERED", "PICKED_UP", "DROP_ENROUTE", "FAILED",
  "CANCELLED", "RTS", "RETURNED", "ASSIGNED", "UNASSIGNED",
  "INSCAN_AT_WAREHOUSE",
];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function MedicalOrderMover() {
  const API_URL = process.env.REACT_APP_DRIVER_API_URL || process.env.REACT_APP_API_URL || "http://localhost:5566/";
  const BASE = API_URL.endsWith("/") ? API_URL + "shypvdriverapp" : API_URL + "/shypvdriverapp";

  // ── partners dropdowns ────────────────────────────────────────
  // sourcePartners = partners that currently OWN medical drops (filter dropdown)
  // targetPartners = full CRM directory (the "move to" dropdown)
  const [sourcePartners, setSourcePartners] = useState([]);
  const [targetPartners, setTargetPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const partnerById = (id) =>
    sourcePartners.find((p) => String(p.id) === String(id)) ||
    targetPartners.find((p) => String(p.id) === String(id));

  // ── filters ───────────────────────────────────────────────────
  const [filterPartner, setFilterPartner] = useState("");
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(today());
  const [status, setStatus] = useState("");
  const [awb, setAwb] = useState("");
  const [limit, setLimit] = useState(200);

  // ── results ───────────────────────────────────────────────────
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());

  // ── move flow ─────────────────────────────────────────────────
  const [toPartner, setToPartner] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveResult, setMoveResult] = useState(null);

  // ── quick-select by group ──────────────────────────────────────
  const [quickField, setQuickField] = useState("partner_name");
  const [quickValue, setQuickValue] = useState("");

  const [error, setError] = useState("");

  // ── load both dropdowns on mount ───────────────────────────────
  useEffect(() => {
    const load = async () => {
      setPartnersLoading(true);
      try {
        const [src, tgt] = await Promise.all([
          axios.get(`${BASE}/api/medical-order-mover/partners`),
          axios.get(`${BASE}/api/medical-order-mover/target-partners`),
        ]);
        setSourcePartners(src.data?.partners || []);
        setTargetPartners(tgt.data?.partners || []);
      } catch (e) {
        setError("Failed to load partners: " + (e?.response?.data?.error || e.message));
      } finally {
        setPartnersLoading(false);
      }
    };
    load();
  }, [BASE]);

  // Build consolidated group options for quick-select dropdown
  const quickGroups = () => {
    const counts = {};
    orders.forEach((o) => {
      const val = String(o[quickField] || "—");
      counts[val] = (counts[val] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  };

  // When user picks a group value → select all matching orders
  const applyQuickSelect = (val) => {
    setQuickValue(val);
    if (!val) return;
    const ids = orders
      .filter((o) => String(o[quickField] || "—") === val)
      .map((o) => o.order_id);
    setSelected(new Set(ids));
  };

  const search = async () => {
    setError(""); setMoveResult(null); setSearched(false); setOrders([]); setSelected(new Set()); setQuickValue("");
    setSearching(true);
    try {
      const body = {
        from_date: fromDate || null,
        to_date: toDate || null,
        partner_id: filterPartner ? Number(filterPartner) : null,
        status: status || null,
        awb: awb.trim() || null,
        limit: Number(limit) || 200,
      };
      const r = await axios.post(`${BASE}/api/medical-order-mover/search`, body);
      setOrders(r.data?.orders || []);
      setSearched(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSearching(false);
    }
  };

  const toggleOne = (orderId) => {
    const n = new Set(selected);
    n.has(orderId) ? n.delete(orderId) : n.add(orderId);
    setSelected(n);
  };
  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(orders.map((o) => o.order_id)));
    else setSelected(new Set());
  };
  const allChecked = orders.length > 0 && orders.every((o) => selected.has(o.order_id));

  // ── selected-orders breakdown for confirm dialog ──────────────
  const breakdown = () => {
    const counts = {};
    orders.filter((o) => selected.has(o.order_id)).forEach((o) => {
      const name = o.partner_name || `partner ${o.partner_id ?? "?"}`;
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  };

  const askConfirm = () => {
    if (selected.size === 0) { setError("Select at least one order"); return; }
    if (!toPartner)         { setError("Pick a target partner");    return; }
    setError("");
    setConfirmOpen(true);
  };

  const doMove = async () => {
    setMoving(true); setError(""); setMoveResult(null);
    try {
      const r = await axios.post(`${BASE}/api/medical-order-mover/move`, {
        order_ids: Array.from(selected),
        to_partner_id: Number(toPartner),
      });
      setMoveResult(r.data);
      setConfirmOpen(false);
      // Refresh the table so newly-moved rows show their new partner.
      await search();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setMoving(false);
    }
  };

  return (
    <div style={{ padding: 20, background: "#f8fafc", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 10 }}>
        <i className="fa fa-exchange" style={{ fontSize: 22, color: "#7c3aed" }} />
        <h2 style={{ margin: 0, fontSize: 22, color: "#0f172a" }}>Medical Order — Partner Mover</h2>
        <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>
          Reassign medical drops (<code>shipment_type=medical, type=DROP</code>) from one partner to another
        </span>
      </div>

      {/* Filter card */}
      <Card title="1. Filter Orders">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 10 }}>
          <Field label="Current partner (only partners with medical drops shown)">
            <select value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)} style={INPUT}>
              <option value="">— Any partner —</option>
              {sourcePartners.map((p) => {
                const label = p.partner_name
                  ? `${p.partner_name}  ·  #${p.id}  ·  ${p.order_count} order(s)`
                  : `(unknown / default)  ·  #${p.id}  ·  ${p.order_count} order(s)`;
                return <option key={p.id} value={p.id}>{label}</option>;
              })}
            </select>
          </Field>
          <Field label="From date">
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={INPUT} />
          </Field>
          <Field label="To date">
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={INPUT} />
          </Field>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={INPUT}>
              {STATUSES.map((s) => <option key={s} value={s}>{s || "— Any status —"}</option>)}
            </select>
          </Field>
          <Field label="AWB / job_id contains">
            <input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="optional" style={INPUT} />
          </Field>
          <Field label="Limit">
            <input type="number" min={1} max={1000} value={limit} onChange={(e) => setLimit(e.target.value)} style={INPUT} />
          </Field>
          <div /><div />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={search} disabled={searching || partnersLoading} style={BTN.primary}>
            {searching ? <><i className="fa fa-spinner fa-spin" /> Searching…</> : <><i className="fa fa-search" /> Search</>}
          </button>
          {partnersLoading && <span style={{ fontSize: 12, color: "#94a3b8" }}>Loading partners…</span>}
        </div>
        {error && <Alert>{error}</Alert>}
      </Card>

      {/* Results card */}
      {searched && (
        <Card
          title={`2. Results — ${orders.length} order(s)`}
          subtitle={selected.size > 0 ? `${selected.size} selected for move` : ""}
        >
          {orders.length === 0
            ? <div style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                No medical drops match these filters
              </div>
            : (
              <>
                {/* ── Quick Select by Group ── */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#0369a1", whiteSpace: "nowrap" }}>
                    ⚡ Quick Select:
                  </span>
                  <select
                    value={quickField}
                    onChange={(e) => { setQuickField(e.target.value); setQuickValue(""); }}
                    style={{ ...INPUT, width: 150, fontSize: 12 }}
                  >
                    <option value="partner_name">By Partner</option>
                    <option value="status">By Status</option>
                    <option value="order_date">By Date</option>
                  </select>
                  <select
                    value={quickValue}
                    onChange={(e) => applyQuickSelect(e.target.value)}
                    style={{ ...INPUT, width: 220, fontSize: 12 }}
                  >
                    <option value="">— pick a group —</option>
                    {quickGroups().map(([val, count]) => (
                      <option key={val} value={val}>{val} ({count})</option>
                    ))}
                  </select>
                  {quickValue && (
                    <button
                      onClick={() => { setQuickValue(""); setSelected(new Set()); }}
                      style={{ ...BTN.ghost, padding: "5px 10px", fontSize: 11 }}
                    >
                      Clear
                    </button>
                  )}
                  <span style={{ fontSize: 11, color: "#0369a1" }}>
                    {selected.size > 0 ? `${selected.size} orders selected` : "Select a group to auto-check all matching rows"}
                  </span>
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={TH}>
                          <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
                        </th>
                        <th style={TH}>order_id</th>
                        <th style={TH}>AWB / job_id</th>
                        <th style={TH}>Current partner</th>
                        <th style={TH}>Status</th>
                        <th style={TH}>Order date</th>
                        <th style={TH}>Receiver</th>
                        <th style={TH}>Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.order_id} style={{ background: selected.has(o.order_id) ? "#eff6ff" : "#fff" }}>
                          <td style={TD}>
                            <input type="checkbox" checked={selected.has(o.order_id)} onChange={() => toggleOne(o.order_id)} />
                          </td>
                          <td style={TD}>{o.order_id}</td>
                          <td style={TD}><code style={{ fontSize: 11 }}>{o.job_id}</code></td>
                          <td style={TD}>
                            <span style={{ fontWeight: 500 }}>{o.partner_name || "—"}</span>
                            <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 4 }}>#{o.partner_id}</span>
                          </td>
                          <td style={TD}><Badge>{o.status}</Badge></td>
                          <td style={{ ...TD, fontFamily: "monospace", fontSize: 11 }}>
                            {String(o.order_date || "").slice(0, 19)}
                          </td>
                          <td style={TD}>{o.receiver_name || o.contact_name || "—"}</td>
                          <td style={{ ...TD, fontSize: 11, color: "#64748b", maxWidth: 300 }}>
                            {(o.formatted_address || "").slice(0, 60)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Move action bar */}
                <div style={{ marginTop: 14, padding: 14, background: "#fef3c7", borderRadius: 8, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <i className="fa fa-exchange" style={{ color: "#92400e" }} />
                  <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                    Move {selected.size} selected order(s) to:
                  </span>
                  <select value={toPartner} onChange={(e) => setToPartner(e.target.value)} style={{ ...INPUT, width: 280 }}>
                    <option value="">— Pick target partner —</option>
                    {targetPartners.map((p) => (
                      <option key={p.id} value={p.id}>{p.partner_name}  ·  #{p.id}</option>
                    ))}
                  </select>
                  <button onClick={askConfirm} disabled={selected.size === 0 || !toPartner || moving} style={BTN.danger}>
                    <i className="fa fa-arrow-right" /> Move
                  </button>
                </div>
              </>
            )}
        </Card>
      )}

      {/* Move result banner */}
      {moveResult && (
        <Card title="Move Complete">
          <div style={{ padding: 12, background: "#f0fdf4", borderRadius: 8, color: "#166534", fontSize: 14, display: "flex", gap: 12, alignItems: "center" }}>
            <i className="fa fa-check-circle" />
            <span><strong>{moveResult.updated}</strong> of {moveResult.requested} order(s) reassigned to <strong>{partnerById(moveResult.to_partner_id)?.partner_name || `partner ${moveResult.to_partner_id}`}</strong>.</span>
          </div>
          {moveResult.from_partner_counts && Object.keys(moveResult.from_partner_counts).length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#475569" }}>
              <strong>Source breakdown:</strong>
              <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                {Object.entries(moveResult.from_partner_counts).map(([fromId, count]) => (
                  <li key={fromId}>
                    {count} order(s) from{" "}
                    <strong>{partnerById(fromId)?.partner_name || `partner ${fromId}`}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* Confirm dialog */}
      {confirmOpen && (
        <div style={MODAL_BG} onClick={() => !moving && setConfirmOpen(false)}>
          <div style={MODAL_BOX} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <i className="fa fa-exclamation-triangle" style={{ color: "#d97706", fontSize: 20 }} />
              <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a" }}>Confirm Partner Move</h3>
            </div>
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#475569" }}>
              You're about to reassign <strong>{selected.size}</strong> medical order(s) to{" "}
              <strong style={{ color: "#0f172a" }}>{partnerById(toPartner)?.partner_name || "?"}</strong>{" "}
              <code style={{ fontSize: 11, color: "#64748b" }}>({partnerById(toPartner)?.partner_code})</code>.
            </p>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: 10, marginBottom: 14, fontSize: 12 }}>
              <strong style={{ color: "#475569" }}>From-partner breakdown:</strong>
              <ul style={{ margin: "6px 0 0 18px", padding: 0, color: "#475569" }}>
                {Object.entries(breakdown()).map(([name, count]) => (
                  <li key={name}>{count} from <strong>{name}</strong></li>
                ))}
              </ul>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "#dc2626" }}>
              This updates <code>new_shipment_job_manager.partner_id</code> immediately and there is no undo button.
              The change is logged server-side for traceability.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setConfirmOpen(false)} disabled={moving} style={BTN.ghost}>Cancel</button>
              <button onClick={doMove} disabled={moving} style={BTN.danger}>
                {moving ? <><i className="fa fa-spinner fa-spin" /> Moving…</> : <><i className="fa fa-check" /> Yes, move {selected.size}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────
function Card({ title, subtitle, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 18, marginBottom: 14 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</label>
      {children}
    </div>
  );
}
function Badge({ children }) {
  return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: "#f1f5f9", color: "#475569" }}>{children}</span>;
}
function Alert({ children }) {
  return (
    <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, background: "#fee2e2", color: "#991b1b", fontSize: 13 }}>
      <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />{children}
    </div>
  );
}

const INPUT = { width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };
const TH = { textAlign: "left", padding: "8px 10px", background: "#f8fafc", color: "#475569", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const TD = { padding: "9px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", verticalAlign: "top" };
const BTN_BASE = { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: "pointer", border: "none" };
const BTN = {
  primary: { ...BTN_BASE, background: "#2563eb", color: "#fff" },
  danger:  { ...BTN_BASE, background: "#dc2626", color: "#fff" },
  ghost:   { ...BTN_BASE, background: "#fff", color: "#475569", border: "1px solid #cbd5e1" },
};
const MODAL_BG = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const MODAL_BOX = { background: "#fff", borderRadius: 12, padding: 20, width: 480, maxWidth: "92vw", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" };
